// The report workspace: tabbed sidebar (sources/figures/refs/tasks) | LaTeX
// editor | PDF preview, with a floating command bar for AI edits. Generation +
// compile run on the server; this page triggers them and polls status.
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  generateReport,
  generateStudySetFromReport,
  getReport,
  recompileReport,
  rewriteSection,
  saveReportLatex,
  type ReportCompileStatus,
  type ReportDocument,
  type RewriteResult,
} from "@/api/report";
import { useProject } from "../use-project";
import { Alert, Button, Spinner } from "@/components/ui";
import { ReportSidebar } from "./report-sidebar";
import { LatexEditorPanel } from "./latex-editor-panel";
import { PdfPreviewPanel } from "./pdf-preview-panel";
import { CommandBar } from "./command-bar";
import { EditReviewModal } from "./edit-review-modal";
import type { CodeMirrorHandle, EditorSelection } from "./codemirror-editor";

type Pane = "index" | "editor" | "preview";
type Target = { from: number; to: number; label: string; sectionIndex?: number };

const AUTO_COMPILE_KEY = "report-auto-compile";

export function ProjectReportPage() {
  const { t } = useTranslation();
  const { projectId = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [pane, setPane] = useState<Pane>("editor");
  const [reportScope, setReportScope] = useState<"broad" | "focused">("broad");
  const [spineFileId, setSpineFileId] = useState("");
  const [reportBrief, setReportBrief] = useState("");
  const [genSetBusy, setGenSetBusy] = useState(false);
  // Figure-library "make a label-the-diagram question here" requests (assetId →
  // instruction), applied on the next study-set generation.
  const [labelReqs, setLabelReqs] = useState<Record<string, string>>({});
  const { data: projectForFiles } = useProject(projectId);

  const editorRef = useRef<CodeMirrorHandle>(null);
  const selectionRef = useRef<EditorSelection | null>(null);
  const [cmdTarget, setCmdTarget] = useState<Target | null>(null);
  const [pending, setPending] = useState<{ oldText: string; result: RewriteResult } | null>(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [activeSectionIndex, setActiveSectionIndex] = useState(-1);
  const [autoCompile, setAutoCompile] = useState(
    () => typeof localStorage !== "undefined" && localStorage.getItem(AUTO_COMPILE_KEY) === "1",
  );

  const { data: report, isLoading } = useQuery({
    queryKey: ["report", projectId],
    queryFn: () => getReport(projectId),
    retry: false,
    refetchInterval: (q) => {
      const d = q.state.data as ReportDocument | undefined;
      if (!d) return false;
      const inflight =
        d.status === "running" || d.status === "pending" || d.compile_status === "compiling";
      return inflight ? 3000 : false;
    },
  });

  const generate = useMutation({
    mutationFn: (opts?: { mode?: "broad" | "focused"; spine_file_id?: string; brief?: string }) =>
      generateReport(projectId, opts),
    onSuccess: (doc) => {
      qc.setQueryData(["report", projectId], doc);
      qc.invalidateQueries({ queryKey: ["report", projectId] });
    },
  });
  const startGenerate = () => {
    const base =
      reportScope === "focused" && spineFileId
        ? { mode: "focused" as const, spine_file_id: spineFileId }
        : { mode: "broad" as const };
    const b = reportBrief.trim();
    generate.mutate(b ? { ...base, brief: b } : base);
  };

  const crossFeedStudySet = async () => {
    setGenSetBusy(true);
    try {
      const label_requests = Object.entries(labelReqs).map(
        ([asset_id, instruction]) => ({ asset_id, instruction }),
      );
      await generateStudySetFromReport(
        projectId,
        label_requests.length ? { label_requests } : undefined,
      );
      navigate(`/app/projects/${projectId}`);
    } catch (e) {
      setEditError(e instanceof Error ? e.message : t("Could not generate a study set."));
      setGenSetBusy(false);
    }
  };

  const save = async (latex: string) => {
    const doc = await saveReportLatex(projectId, latex);
    qc.setQueryData(["report", projectId], doc);
  };
  const compile = async () => {
    const doc = await recompileReport(projectId);
    qc.setQueryData(["report", projectId], doc);
    qc.invalidateQueries({ queryKey: ["report", projectId] });
  };

  // Re-sync the editor + cache to server truth after a server-side doc change
  // (asset insert, exercise solve).
  const refreshDoc = async () => {
    const doc = await getReport(projectId);
    qc.setQueryData(["report", projectId], doc);
    editorRef.current?.setDoc(doc.latex_source || "");
  };
  const refetchReport = () => qc.invalidateQueries({ queryKey: ["report", projectId] });

  const openCommand = () => {
    const handle = editorRef.current;
    if (!handle) return;
    const sel = selectionRef.current || handle.getSelection();
    if (sel) {
      setCmdTarget({ from: sel.from, to: sel.to, label: t("selected text") });
      return;
    }
    const sec = handle.getEnclosingSection();
    if (sec) {
      setCmdTarget({ from: sec.from, to: sec.to, label: sec.title || t("this section") });
    } else {
      setEditError(t("Place the cursor in a section, or select some text, then edit."));
    }
  };

  // Cmd/Ctrl-K opens the command bar on the section at the cursor.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (report?.status === "complete") openCommand();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.status]);

  const submitEdit = async (directive: string) => {
    const handle = editorRef.current;
    if (!handle || !cmdTarget) return;
    setEditBusy(true);
    setEditError(null);
    try {
      // Flush the editor's current text to the server so the rewrite operates on
      // the latest source.
      await save(handle.getDoc());
      const result = await rewriteSection(projectId, {
        // Section scope (from the outline) edits the whole \section; otherwise
        // edit the targeted char range.
        ...(cmdTarget.sectionIndex != null
          ? { section_index: cmdTarget.sectionIndex }
          : { char_start: cmdTarget.from, char_end: cmdTarget.to }),
        directive,
        preview: true,
      });
      const oldText = handle.getDoc().slice(result.target_start, result.target_end);
      setPending({ oldText, result });
      setCmdTarget(null);
    } catch (e) {
      setEditError(e instanceof Error ? e.message : t("The edit failed."));
    } finally {
      setEditBusy(false);
    }
  };

  const acceptEdit = () => {
    const handle = editorRef.current;
    const r = pending?.result;
    if (handle && r && r.proposed_latex != null) {
      handle.replaceRange(r.target_start, r.target_end, r.proposed_latex);
    }
    setPending(null);
  };

  // Outline → editor interactions.
  const goToSection = (index: number) => {
    setPane("editor");
    editorRef.current?.scrollToSection(index);
    setActiveSectionIndex(index);
  };
  const editSection = (index: number, title: string) => {
    setPane("editor");
    editorRef.current?.scrollToSection(index);
    setCmdTarget({ from: 0, to: 0, label: title || t("this section"), sectionIndex: index });
  };
  const insertCite = (key: string) => {
    editorRef.current?.insertAtCursor(`\\cite{${key}}`);
  };

  const toggleAutoCompile = () => {
    setAutoCompile((v) => {
      const next = !v;
      try {
        localStorage.setItem(AUTO_COMPILE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  // The PDF is stale when the source changed since the last compile.
  const stale = report?.compile_status === "pending" && report?.status === "complete";

  const header = (
    <Link to={`/app/projects/${projectId}`} className="text-sm text-brand-600 hover:underline">
      {t("← Back to project")}
    </Link>
  );

  if (isLoading) {
    return <div className="flex justify-center py-16"><Spinner /></div>;
  }

  if (!report) {
    return (
      <div className="space-y-6">
        {header}
        <section className="card text-center">
          <h1 className="text-xl font-bold text-ink">{t("Write a report from this project")}</h1>
          <p className="mx-auto mt-2 max-w-prose text-sm text-ink-soft">
            {t("The agent reads every source, builds a lookup index of what each file contributes, then drafts a full LaTeX report you can edit and compile here.")}
          </p>
          {generate.isError && <Alert>{t("Could not start report generation.")}</Alert>}
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-center gap-2 text-sm">
              <span className="text-ink-soft">{t("Scope")}:</span>
              <div className="flex overflow-hidden rounded-lg border border-black/10 text-xs">
                {(["broad", "focused"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setReportScope(s)}
                    className={
                      "px-2.5 py-1 capitalize " +
                      (reportScope === s ? "bg-brand-600 text-white" : "bg-white text-ink-soft hover:bg-black/5")
                    }
                  >
                    {t(s)}
                  </button>
                ))}
              </div>
            </div>
            {reportScope === "focused" && (
              <select
                value={spineFileId}
                onChange={(e) => setSpineFileId(e.target.value)}
                className="mx-auto block w-full max-w-xs rounded-lg border border-black/10 px-3 py-2 text-sm"
              >
                <option value="">{t("Focus on a file (spine)…")}</option>
                {(projectForFiles?.files ?? [])
                  .filter((f) => f.role !== "ignore")
                  .map((f) => (
                    <option key={f.id} value={f.id}>{f.filename}</option>
                  ))}
              </select>
            )}
            <textarea
              value={reportBrief}
              onChange={(e) => setReportBrief(e.target.value)}
              rows={2}
              placeholder={t("Brief (optional) — what you want, the angle, emphasis")}
              className="mx-auto block w-full max-w-xs rounded-lg border border-black/10 px-3 py-2 text-sm"
            />
            <Button onClick={startGenerate} disabled={generate.isPending}>
              {generate.isPending ? t("Starting…") : t("Generate report")}
            </Button>
          </div>
        </section>
      </div>
    );
  }

  if (report.status === "running" || report.status === "pending") {
    return (
      <div className="space-y-6">
        {header}
        <section className="card space-y-3">
          <div className="flex items-center gap-2">
            <Spinner className="h-4 w-4" />
            <span className="text-sm font-medium text-ink">
              {t("Reading your sources and drafting the report…")}
            </span>
          </div>
          <p className="text-xs text-ink-faint">
            {t("This can take a few minutes. You can leave and come back.")}
          </p>
        </section>
      </div>
    );
  }

  if (report.status === "failed") {
    return (
      <div className="space-y-6">
        {header}
        <Alert>{report.error_message || t("Report generation failed.")}</Alert>
        <Button onClick={() => generate.mutate({ mode: "broad" })} disabled={generate.isPending}>{t("Try again")}</Button>
      </div>
    );
  }

  // Complete → the workspace.
  return (
    <div className="flex h-[calc(100vh-7rem)] flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        {header}
        <div className="flex items-center gap-3">
          {stale ? (
            <Button size="sm" onClick={() => void compile()}>
              {t("PDF outdated — Compile")}
            </Button>
          ) : (
            <CompileBadge status={report.compile_status} />
          )}
          <label className="flex items-center gap-1 text-xs text-ink-soft">
            <input type="checkbox" checked={autoCompile} onChange={toggleAutoCompile} />
            {t("Auto-compile")}
          </label>
          <Button size="sm" variant="secondary" onClick={() => report.status === "complete" && openCommand()}>
            {t("Edit (⌘K)")}
          </Button>
          <Button size="sm" variant="secondary" onClick={crossFeedStudySet} disabled={genSetBusy}>
            {genSetBusy ? t("Starting…") : t("Generate study set")}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => generate.mutate({ mode: "broad" })} disabled={generate.isPending}>
            {t("Regenerate")}
          </Button>
        </div>
      </div>

      {editError && <Alert>{editError}</Alert>}

      <div className="flex gap-1 lg:hidden">
        {(["index", "editor", "preview"] as Pane[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPane(p)}
            className={`flex-1 rounded-md px-2 py-1 text-xs font-medium ${
              pane === p ? "bg-brand-600 text-white" : "bg-zinc-100 text-zinc-600"
            }`}
          >
            {p === "index" ? t("Panels") : p === "editor" ? t("LaTeX") : t("Preview")}
          </button>
        ))}
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[20rem_minmax(0,1fr)_minmax(0,1fr)]">
        <div className={`min-h-0 rounded-xl border border-black/10 bg-white ${pane === "index" ? "" : "hidden"} lg:block`}>
          <ReportSidebar
            projectId={projectId}
            report={report}
            activeSectionIndex={activeSectionIndex}
            onChanged={refetchReport}
            onDocChanged={refreshDoc}
            onGoToSection={goToSection}
            onEditSection={editSection}
            onInsertCite={insertCite}
            labelReqs={labelReqs}
            onLabelReqs={setLabelReqs}
          />
        </div>
        <div className={`relative min-h-0 rounded-xl border border-black/10 bg-white ${pane === "editor" ? "" : "hidden"} lg:block`}>
          <LatexEditorPanel
            documentKey={report.id}
            initialValue={report.latex_source || ""}
            compileStatus={report.compile_status}
            autoCompile={autoCompile}
            onSave={save}
            onCompile={compile}
            editorRef={editorRef}
            onSelectionChange={(sel) => {
              selectionRef.current = sel;
              setActiveSectionIndex(editorRef.current?.getActiveSectionIndex() ?? -1);
            }}
          />
          {cmdTarget && (
            <CommandBar
              targetLabel={cmdTarget.label}
              busy={editBusy}
              onSubmit={submitEdit}
              onClose={() => setCmdTarget(null)}
            />
          )}
        </div>
        <div className={`min-h-0 rounded-xl border border-black/10 bg-white ${pane === "preview" ? "" : "hidden"} lg:block`}>
          {report.compile_status === "error" || report.compile_status === "timeout" ? (
            <CompileError log={report.compile_log} url={report.pdf_url} />
          ) : (
            <PdfPreviewPanel url={report.pdf_url} />
          )}
        </div>
      </div>

      {pending && (
        <EditReviewModal
          oldText={pending.oldText}
          newText={pending.result.proposed_latex || ""}
          rationale={pending.result.rationale}
          citationsAdded={pending.result.citations_added}
          busy={false}
          onAccept={acceptEdit}
          onReject={() => setPending(null)}
        />
      )}
    </div>
  );
}

function CompileBadge({ status }: { status: ReportCompileStatus }) {
  const { t } = useTranslation();
  const map: Record<ReportCompileStatus, { label: string; cls: string }> = {
    pending: { label: t("Not compiled"), cls: "bg-zinc-100 text-zinc-600" },
    compiling: { label: t("Compiling…"), cls: "bg-amber-100 text-amber-700" },
    success: { label: t("Compiled"), cls: "bg-emerald-100 text-emerald-700" },
    error: { label: t("Compile error"), cls: "bg-rose-100 text-rose-700" },
    timeout: { label: t("Compile timed out"), cls: "bg-rose-100 text-rose-700" },
    unavailable: { label: t("Compiler unavailable"), cls: "bg-zinc-100 text-zinc-500" },
  };
  const s = map[status];
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${s.cls}`}>{s.label}</span>;
}

function CompileError({ log, url }: { log: string | null; url: string | null }) {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-black/5 px-3 py-2 text-xs font-medium text-rose-700">
        {t("The LaTeX did not compile. Fix the source and press Compile again.")}
      </div>
      <pre className="flex-1 overflow-auto bg-zinc-900 p-3 text-[11px] leading-snug text-zinc-100">
        {log || t("No compile log available.")}
      </pre>
      {url && (
        <div className="border-t border-black/5 p-2 text-center">
          <a href={url} target="_blank" rel="noreferrer" className="text-xs text-brand-600 hover:underline">
            {t("Open last successful PDF")}
          </a>
        </div>
      )}
    </div>
  );
}
